import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    Tooltip,
    Divider,
    Typography,
    Avatar,
    useMediaQuery,
} from "@mui/material";
import { parseISO, isValid as isValidDate } from "date-fns";
import moment from "moment-timezone";
import { useTheme } from "@emotion/react";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WaveguideSummary from "./WaveguideSummary";
import WaveguideWorkOrderDialog from "./WaveguideWorkOrderDialog";

import honeycombOptions from "../../../json/honeycombOptions.json";
import WaveguideDataService from "../../../services/Waveguide.services";

// Helper function to convert decimal to fraction
function decimalToFraction(decimal) {
    if (decimal === null || decimal === undefined || decimal === 0) {
        return "0";
    }

    // Handle negative numbers
    const isNegative = decimal < 0;
    decimal = Math.abs(decimal);

    // If it's already a whole number, return as is
    if (decimal % 1 === 0) {
        return isNegative ? `-${decimal}` : `${decimal}`;
    }

    // Common fractions for typical measurements
    const commonFractions = [
        { decimal: 0.125, fraction: "1/8" },
        { decimal: 0.1875, fraction: "3/16" },
        { decimal: 0.25, fraction: "1/4" },
        { decimal: 0.375, fraction: "3/8" },
        { decimal: 0.5, fraction: "1/2" },
        { decimal: 0.625, fraction: "5/8" },
        { decimal: 0.75, fraction: "3/4" },
        { decimal: 0.875, fraction: "7/8" },
    ];

    // Check for exact matches with common fractions
    const exactMatch = commonFractions.find(
        (f) => Math.abs(f.decimal - decimal) < 0.001
    );
    if (exactMatch) {
        return isNegative ? `-${exactMatch.fraction}` : exactMatch.fraction;
    }

    // Check for mixed numbers (whole number + fraction)
    const wholeNumber = Math.floor(decimal);
    const fractionalPart = decimal - wholeNumber;

    if (wholeNumber > 0) {
        const fractionMatch = commonFractions.find(
            (f) => Math.abs(f.decimal - fractionalPart) < 0.001
        );
        if (fractionMatch) {
            const result = `${wholeNumber} ${fractionMatch.fraction}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If no common fraction found, use a more general approach
    // Convert to fraction with reasonable denominator
    const tolerance = 0.001;
    for (let denominator = 2; denominator <= 64; denominator++) {
        const numerator = Math.round(decimal * denominator);
        if (Math.abs(decimal - numerator / denominator) < tolerance) {
            // Simplify the fraction
            const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
            const commonDivisor = gcd(numerator, denominator);
            const simplifiedNumerator = numerator / commonDivisor;
            const simplifiedDenominator = denominator / commonDivisor;

            if (simplifiedDenominator === 1) {
                return isNegative
                    ? `-${simplifiedNumerator}`
                    : `${simplifiedNumerator}`;
            }

            const result = `${simplifiedNumerator}/${simplifiedDenominator}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If all else fails, return the decimal rounded to 3 places
    return isNegative ? `-${decimal.toFixed(3)}` : decimal.toFixed(3);
}

// Helper function to match honeycomb types
function getHoneycombTypeKey(honeycombType) {
    if (!honeycombType || (!honeycombType.cell && !honeycombType.thickness)) {
        return "unknown";
    }

    // Find matching honeycomb option
    const matchingOption = honeycombOptions.find(
        (option) =>
            Math.abs(option.cell - (honeycombType.cell || 0)) < 0.001 &&
            Math.abs(option.thickness - (honeycombType.thickness || 0)) < 0.001
    );

    if (matchingOption) {
        return `${matchingOption.cell}_${matchingOption.thickness}_${matchingOption.material}`;
    }

    // If no exact match found, create a key from the available data
    return `${honeycombType.cell || "unknown"}_${honeycombType.thickness ||
        "unknown"}_unknown`;
}

// Helper function to get honeycomb display label
function getHoneycombDisplayLabel(honeycombType) {
    if (!honeycombType || (!honeycombType.cell && !honeycombType.thickness)) {
        return "Unknown";
    }

    const cell = honeycombType.cell
        ? `${decimalToFraction(honeycombType.cell)}" cell`
        : "";
    const thickness = honeycombType.thickness
        ? `${decimalToFraction(honeycombType.thickness)}" thick`
        : "";

    return [cell, thickness].filter(Boolean).join(" x ");
}

// Temporary normalization function to convert JSON data to expected format
function normalizeWaveguides(data) {
    const toNumber = (val) => {
        if (val === null || val === undefined || val === "") return null;
        const parsed = Number(val);
        return Number.isNaN(parsed) ? null : parsed;
    };

    return data.map((w, idx) => ({
        id: w.id || idx + 1,
        project: {
            id: w.project && w.project.id ? w.project.id : "",
            number: w.project && w.project.number ? w.project.number : "",
            name: w.project && w.project.name ? w.project.name : "",
        },
        man_bars: !!w.man_bars,
        list: Array.isArray(w.items)
            ? w.items.map((item, i) => ({
                  id: i + 1,
                  size: {
                      width: toNumber(item.width),
                      height: toNumber(item.height),
                      diameter: toNumber(item.diameter),
                  },
                  qty: Number(item.quantity) || 0,
                  total_area_sq_in: Number(item.total_area_sq_in) || 0,
                  perimeter_in: Number(item.perimeter_in) || 0,
              }))
            : [],
        due: w.due || w.due_date || null,
        due_date: w.due || w.due_date || null,
        notes: w.notes || "",
        comments: w.notes || "",
        drawing:
            (w.drawings &&
                w.drawings[0] &&
                (w.drawings[0].file_url || w.drawings[0].document)) ||
            "",
        drawings: Array.isArray(w.drawings) ? w.drawings : [],
        frame_type: w.frame && w.frame.description ? w.frame.description : "",
        work_order_number: w.work_order_number || "",
        honeycomb_type: {
            cell:
                w.honeycomb && w.honeycomb.cell
                    ? toNumber(w.honeycomb.cell)
                    : null,
            thickness:
                w.honeycomb && w.honeycomb.thickness
                    ? toNumber(w.honeycomb.thickness)
                    : null,
        },
        completed: !!w.is_complete,
        is_complete: !!w.is_complete,
        created: w.created || null,
        created_by: w.created_by || null,
        updated: w.updated || null,
        updated_by: w.updated_by || null,
        created_date: w.created || w.updated || null,
        completed_date: w.completed || w.completed_date || null,
        checked_by: w.checked_by || null,
        checked: w.checked || w.checked_at || null,
        checked_at: w.checked || w.checked_at || null,
        assignee: w.assignee || null,
        archived: !!w.is_archived,
        is_archived: !!w.is_archived,
        archived_date: w.archived || w.archived_at || null,
        sheets_required:
            w.summary && w.summary.sheets_required !== undefined
                ? w.summary.sheets_required
                : w.sheets_required !== undefined && w.sheets_required !== null
                ? w.sheets_required
                : 0,
    }));
}

// Simple avatar helper for initials
function stringAvatar(name = "") {
    const trimmed = name.trim();
    const first = trimmed.split(" ")[0] || "";
    const initial = first.charAt(0) || "";
    return {
        children: initial,
        sx: {
            bgcolor: "primary.light",
            color: "primary.contrastText",
        },
    };
}

// Due Date / Completion Chip Component (keeping it here for now)
function DueDateChip({ value, completed, completedDate, checkedBy }) {
    const theme = useTheme();

    const currentDate = useMemo(
        () => moment.tz(new Date(), "America/New_York").toDate(),
        []
    );

    // If completed, show completion information
    if (completed) {
        const completedParsed = completedDate ? parseISO(completedDate) : null;

        return (
            <Box
                sx={{
                    width: 120,
                    height: 48,
                    border: 1,
                    borderColor: "success.main",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                }}
            >
                <CheckCircleIcon
                    sx={{
                        fontSize: "1.2rem",
                        mr: 1,
                        color: "success.main",
                        flexShrink: 0,
                    }}
                />
                <Stack
                    spacing={0.2}
                    alignItems="flex-start"
                    sx={{ width: "100%", minHeight: 0 }}
                    justifyContent="center"
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                            color: "success.main",
                        }}
                    >
                        Completed
                    </Typography>
                    <Divider sx={{ width: "100%" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            fontWeight: 400,
                            fontSize: "0.65rem",
                            lineHeight: 1.1,
                        }}
                    >
                        {completedParsed && isValidDate(completedParsed)
                            ? moment(completedParsed).format("MM/DD/YYYY")
                            : "—"}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // Original due date logic for incomplete items
    const parsed = value ? parseISO(value) : null;
    const delta =
        parsed && isValidDate(parsed)
            ? Math.ceil(
                  (parsed.getTime() - currentDate.getTime()) /
                      (1000 * 3600 * 24)
              )
            : null;

    let dueMessage = "No Due Date";
    if (delta !== null) {
        if (delta === 0) dueMessage = "Today";
        else if (delta === 1) dueMessage = "Tomorrow";
        else if (delta === -1) dueMessage = "Yesterday";
        else if (delta > 1) dueMessage = `In ${delta} days`;
        else dueMessage = `${Math.abs(delta)} days ago`;
    }

    return (
        <Box
            sx={{
                width: 120,
                height: 48,
                border: 1,
                borderColor:
                    delta !== null && delta < 0
                        ? "warning.main"
                        : "primary.main",
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
            }}
        >
            <EventAvailableIcon
                sx={{
                    fontSize: "1.2rem",
                    mr: 1,
                    color:
                        delta !== null && delta < 0
                            ? "warning.main"
                            : "primary.main",
                    flexShrink: 0,
                }}
            />
            <Stack
                spacing={0.2}
                alignItems="flex-start"
                sx={{ width: "100%", minHeight: 0 }}
                justifyContent="center"
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        color:
                            delta !== null && delta < 0
                                ? "warning.main"
                                : "primary.main",
                    }}
                >
                    {dueMessage}
                </Typography>
                <Divider sx={{ width: "100%" }} />
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 400,
                        fontSize: "0.65rem",
                        lineHeight: 1.1,
                    }}
                >
                    {parsed && isValidDate(parsed)
                        ? moment(parsed).format("MM/DD/YYYY")
                        : "—"}
                </Typography>
            </Stack>
        </Box>
    );
}

// Waveguide Order List Component
function WaveguideOrderList({ handleOpenSnackbar, darkState, user, token }) {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [openWorkOrder, setOpenWorkOrder] = useState(false);

    useEffect(() => {
        if (!token) {
            setItems([]);
            return;
        }

        setIsLoading(true);
        WaveguideDataService.getAllWaveguides(token)
            .then((response) => {
                const apiData = Array.isArray(response.data)
                    ? response.data
                    : [];
                setItems(normalizeWaveguides(apiData));
            })
            .catch((error) => {
                console.error("Failed to load waveguides", error);
                setItems([]);
                if (handleOpenSnackbar) {
                    handleOpenSnackbar(
                        "error",
                        "Unable to load waveguides right now."
                    );
                }
            })
            .finally(() => setIsLoading(false));
    }, [token, handleOpenSnackbar]);

    const visibleItems = items.filter((w) => !w.archived);
    const incompleteItems = visibleItems.filter((w) => !w.completed);

    const totalQty = useMemo(
        () =>
            visibleItems.reduce((sum, w) => {
                return (
                    sum +
                    w.list.reduce((acc, li) => acc + (Number(li.qty) || 0), 0)
                );
            }, 0),
        [visibleItems]
    );

    const totalArea = useMemo(
        () =>
            visibleItems.reduce((sum, w) => {
                return (
                    sum +
                    w.list.reduce(
                        (acc, li) => acc + (Number(li.total_area_sq_in) || 0),
                        0
                    )
                );
            }, 0),
        [visibleItems]
    );

    const incompleteQty = useMemo(
        () =>
            incompleteItems.reduce((sum, w) => {
                return (
                    sum +
                    w.list.reduce((acc, li) => acc + (Number(li.qty) || 0), 0)
                );
            }, 0),
        [incompleteItems]
    );

    const incompleteArea = useMemo(
        () =>
            incompleteItems.reduce((sum, w) => {
                return (
                    sum +
                    w.list.reduce(
                        (acc, li) => acc + (Number(li.total_area_sq_in) || 0),
                        0
                    )
                );
            }, 0),
        [incompleteItems]
    );

    // Calculate area by honeycomb type for all visible items
    const areaByHoneycombType = useMemo(() => {
        const breakdown = {};

        visibleItems.forEach((w) => {
            const honeycombKey = getHoneycombTypeKey(w.honeycomb_type);
            const honeycombLabel = getHoneycombDisplayLabel(w.honeycomb_type);

            if (!breakdown[honeycombKey]) {
                breakdown[honeycombKey] = {
                    label: honeycombLabel,
                    totalArea: 0,
                    openArea: 0,
                    totalPerimeter: 0,
                    openPerimeter: 0,
                    totalQty: 0,
                    openQty: 0,
                    totalOrders: 0,
                    openOrders: 0,
                };
            }

            const orderTotalArea = w.list.reduce(
                (acc, li) => acc + (Number(li.total_area_sq_in) || 0),
                0
            );
            const orderTotalPerimeter = w.list.reduce(
                (acc, li) => acc + (Number(li.perimeter_in) || 0),
                0
            );
            const orderTotalQty = w.list.reduce(
                (acc, li) => acc + (Number(li.qty) || 0),
                0
            );

            breakdown[honeycombKey].totalArea += orderTotalArea;
            breakdown[honeycombKey].totalPerimeter += orderTotalPerimeter;
            breakdown[honeycombKey].totalQty += orderTotalQty;
            breakdown[honeycombKey].totalOrders += 1;

            if (!w.completed) {
                breakdown[honeycombKey].openArea += orderTotalArea;
                breakdown[honeycombKey].openPerimeter += orderTotalPerimeter;
                breakdown[honeycombKey].openQty += orderTotalQty;
                breakdown[honeycombKey].openOrders += 1;
            }
        });

        return breakdown;
    }, [visibleItems]);

    const incompleteCount = incompleteItems.length;
    const totalCount = visibleItems.length;

    const openWO = (order) => {
        setSelected(order);
        setOpenWorkOrder(true);
    };
    const closeWO = () => {
        setOpenWorkOrder(false);
        setSelected(null);
    };

    const replaceAndSort = (updatedItem) =>
        setItems((prev) => {
            const withUpdate = prev.map((w) =>
                w.id === updatedItem.id ? updatedItem : w
            );
            return [...withUpdate].sort(
                (a, b) => Number(a.completed) - Number(b.completed)
            );
        });

    const updateDue = async (orderId, iso) => {
        if (!token) {
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "No token available to update waveguide."
                );
            return;
        }
        try {
            const response = await WaveguideDataService.updateDueDate(
                orderId,
                { due: iso },
                token
            );
            const updated =
                response && response.data
                    ? normalizeWaveguides([response.data])[0]
                    : null;
            if (updated) {
                replaceAndSort(updated);
            }
            handleOpenSnackbar &&
                handleOpenSnackbar("success", "Due date updated");
        } catch (error) {
            console.error("Failed to update due date", error);
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "Unable to update due date right now."
                );
        }
    };

    const markChecked = async (orderId) => {
        if (!token) {
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "No token available to update waveguide."
                );
            return;
        }
        try {
            const response = await WaveguideDataService.toggleChecked(
                orderId,
                token
            );
            const updated =
                response && response.data
                    ? normalizeWaveguides([response.data])[0]
                    : null;
            if (updated) {
                replaceAndSort(updated);
            }
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "success",
                    "Waveguide order marked as checked and completed"
                );
        } catch (error) {
            console.error("Failed to mark waveguide checked", error);
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "Unable to update waveguide right now."
                );
        } finally {
            closeWO();
        }
    };

    const markUnchecked = async (orderId) => {
        if (!token) {
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "No token available to update waveguide."
                );
            return;
        }
        try {
            const response = await WaveguideDataService.toggleChecked(
                orderId,
                token
            );
            const updated =
                response && response.data
                    ? normalizeWaveguides([response.data])[0]
                    : null;
            if (updated) {
                replaceAndSort(updated);
            }
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "info",
                    "Waveguide order unchecked and marked as incomplete"
                );
        } catch (error) {
            console.error("Failed to uncheck waveguide", error);
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "Unable to update waveguide right now."
                );
        } finally {
            closeWO();
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
                px: 0,
                py: { xs: 1, sm: 2, md: 4 },
            }}
        >
            <Box sx={{ mb: 0.5, width: "100%" }}>
                <WaveguideSummary
                    darkState={darkState}
                    totalCount={totalCount}
                    openCount={incompleteCount}
                    totalQty={totalQty}
                    openQty={incompleteQty}
                    totalArea={totalArea}
                    openArea={incompleteArea}
                    honeycombBreakdown={areaByHoneycombType}
                    rawWaveguideData={visibleItems}
                />
            </Box>

            {isLoading && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 2 }}
                >
                    Loading waveguides...
                </Typography>
            )}

            {!isLoading && visibleItems.length === 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 2 }}
                >
                    No waveguides to display.
                </Typography>
            )}

            {visibleItems.length > 0 && (
                <List
                    sx={{
                        width: "100%",
                        bgcolor: "background.paper",
                        border: darkState ? "1.5px solid" : "3px solid",
                        borderRadius: 2,
                        pt: 0,
                        pb: 0,
                        borderColor: "primary.main",
                        overflowX: "hidden",
                    }}
                >
                    {visibleItems.map((order, i) => {
                        const assigneeName = order.assignee
                            ? `${order.assignee.first_name || ""} ${order
                                  .assignee.last_name || ""}`.trim()
                            : "";

                        const itemCount = order.list.length;
                        const qtySum = order.list.reduce(
                            (acc, li) => acc + (Number(li.qty) || 0),
                            0
                        );
                        const sheetsRequired =
                            order.sheets_required !== undefined
                                ? order.sheets_required
                                : null;
                        const projectLike = order.project || {};

                        let honeycombLabel = "";
                        if (
                            (order.honeycomb_type &&
                                order.honeycomb_type.cell) ||
                            (order.honeycomb_type &&
                                order.honeycomb_type.thickness)
                        ) {
                            const cell =
                                order.honeycomb_type &&
                                order.honeycomb_type.cell
                                    ? `${decimalToFraction(
                                          order.honeycomb_type.cell
                                      )}" cell`
                                    : "";
                            const thick =
                                order.honeycomb_type &&
                                order.honeycomb_type.thickness
                                    ? `${decimalToFraction(
                                          order.honeycomb_type.thickness
                                      )}" thick`
                                    : "";
                            honeycombLabel = [cell, thick]
                                .filter(Boolean)
                                .join(" x ");
                        }
                        const frameText = order.frame_type || "";

                        const dueChip = (
                            <DueDateChip
                                value={order.due}
                                completed={order.completed}
                                completedDate={order.completed_date}
                                checkedBy={order.checked_by}
                            />
                        );

                        return (
                            <div key={order.id}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        !isMobile && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ pr: 1 }}
                                            >
                                                {dueChip}
                                            </Stack>
                                        )
                                    }
                                >
                                    <ListItemButton
                                        onClick={() => openWO(order)}
                                        sx={{
                                            position: "relative",
                                            overflow: "hidden",
                                            minWidth: 0,
                                            opacity: order.completed ? 0.45 : 1,
                                            bgcolor: order.completed
                                                ? "action.hover"
                                                : "background.paper",
                                            borderLeft: order.completed
                                                ? 4
                                                : undefined,
                                            borderLeftColor: order.completed
                                                ? "success.light"
                                                : undefined,
                                        }}
                                    >
                                        {order.completed && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    width: 4,
                                                    bgcolor: "success.main",
                                                    zIndex: 3,
                                                }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                left: 6,
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                bgcolor: order.completed
                                                    ? "success.main"
                                                    : "warning.main",
                                                zIndex: 2,
                                            }}
                                        />

                                        <ListItemText
                                            primaryTypographyProps={{
                                                component: "div",
                                                variant: "subtitle1",
                                                sx: {
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                },
                                            }}
                                            primary={
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ overflow: "hidden" }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        variant="filled"
                                                        color="primary"
                                                        label={
                                                            projectLike.number ||
                                                            "—"
                                                        }
                                                        sx={{ maxWidth: 120 }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={700}
                                                        noWrap
                                                        sx={{ maxWidth: "60%" }}
                                                    >
                                                        {projectLike.name ||
                                                            projectLike.number ||
                                                            order.work_order_number}
                                                    </Typography>
                                                </Stack>
                                            }
                                            secondaryTypographyProps={{
                                                component: "div",
                                            }}
                                            secondary={
                                                <>
                                                    <Stack
                                                        spacing={0.25}
                                                        sx={{
                                                            wordBreak:
                                                                "break-word",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.primary"
                                                        >
                                                            WO#:{" "}
                                                            {order.work_order_number ||
                                                                "—"}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Items: {itemCount} |
                                                            Qty: {qtySum}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {frameText
                                                                ? `Frame: ${frameText}`
                                                                : "Frame: —"}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {honeycombLabel
                                                                ? `Honeycomb: ${honeycombLabel}`
                                                                : "Honeycomb: —"}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Stock sheets
                                                            required:{" "}
                                                            {sheetsRequired !==
                                                            null
                                                                ? sheetsRequired
                                                                : "—"}
                                                        </Typography>
                                                        {assigneeName ? (
                                                            <Tooltip
                                                                title={`Assigned to ${assigneeName}`}
                                                                disableFocusListener
                                                                disableTouchListener
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        mt: 3,
                                                                        px: 1,
                                                                        py: 0.75,
                                                                        width:
                                                                            "fit-content",
                                                                        alignSelf:
                                                                            "flex-start",
                                                                        border: 1,
                                                                        borderColor:
                                                                            "divider",
                                                                        borderRadius: 1,
                                                                        bgcolor:
                                                                            "background.default",
                                                                        display:
                                                                            "inline-flex",
                                                                        alignItems:
                                                                            "center",
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        spacing={
                                                                            0.75
                                                                        }
                                                                        alignItems="center"
                                                                    >
                                                                        {(() => {
                                                                            const avatarProps = stringAvatar(
                                                                                assigneeName
                                                                            );
                                                                            return (
                                                                                <Avatar
                                                                                    {...avatarProps}
                                                                                    sx={{
                                                                                        ...avatarProps.sx,
                                                                                        width: 28,
                                                                                        height: 28,
                                                                                        fontSize: 12,
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })()}
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            noWrap
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                assigneeName
                                                                            }
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                    {isMobile && (
                                                        <Box sx={{ mt: 2 }}>
                                                            {dueChip}
                                                        </Box>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>

                                {i < visibleItems.length - 1 && (
                                    <Divider
                                        sx={{ borderColor: "primary.main" }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </List>
            )}

            <WaveguideWorkOrderDialog
                open={openWorkOrder}
                onClose={closeWO}
                order={selected}
                user={user}
                onMarkChecked={markChecked}
                onMarkUnchecked={markUnchecked}
            />
        </Box>
    );
}

export default WaveguideOrderList;
